import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import CustomModal from '@/components/CustomModal/CustomModal';
import { useInviteToNote } from '@/services/noteGoApi/noteGoApiComponents';
import { useAuthStore } from '@/stores/authStore';
import { useParams } from 'react-router-dom';
import { TimeLimit } from '@/types/invite';
import useAlertStore from '@/stores/alertStore';
import { BETA_ORGANIZATIONS_FEATURE_ENABLED } from '@/features/betaOrganizations/config';
import { useOrganizations } from '@/features/betaOrganizations/hooks/useOrganizations';
import { useOrganizationFolders } from '@/features/betaOrganizations/hooks/useOrganizationFolders';
import { reconcileNoteFolder } from '@/features/betaOrganizations/utils/folderMembership';
import FolderShareModal from '@/features/betaOrganizations/components/FolderShareModal';
import type { OrganizationFolder } from '@/features/betaOrganizations/types';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';

interface InviteModalProps {
  open: boolean;
  handleClose: () => void;
}

const InviteModal: FC<InviteModalProps> = ({ open, handleClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Modes: invite by email, public link, or share with team
  const [shareMode, setShareMode] = useState<'email' | 'public' | 'team'>('public');
  // Invite by email state
  const [accessDuration, setAccessDuration] = useState<TimeLimit | 'unlimited'>('unlimited');
  const [emailAddresses, setEmailAddresses] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  // Public share state
  const [publicUrl, setPublicUrl] = useState('');
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [isPublicLoading, setIsPublicLoading] = useState(false);
  // Team share state
  const [isTeamSharing, setIsTeamSharing] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string>('general');
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const showAlert = useAlertStore((state) => state.showAlert);
  const { mutateAsync: inviteToNote } = useInviteToNote({
    onSuccess: () => {
      showAlert('Invitation sent successfully', 'success');
      handleClose();
      setIsInviting(false);
      setEmailAddresses('');
      setAccessDuration('unlimited');
    },
    onError: (error: unknown) => {
      showAlert(
        getApiErrorMessage(error, 'Something went wrong while sending the invitation. Try again later.'),
        'error',
      );
      setIsInviting(false);
    },
  });
  const { userToken, userData } = useAuthStore();
  const { noteId } = useParams();
  // Membership-based gating (mirrors NotesContent): feature flag + the user
  // belongs to at least one team. Any team member can move their own notes.
  // Gate on `open`: NoteHeader keeps this modal mounted at all times (so its
  // state survives close/reopen), so an ungated hook fired the teams + folders
  // requests on every single note page load, even for users who never open Share.
  const teamQueriesEnabled = BETA_ORGANIZATIONS_FEATURE_ENABLED && open;
  const { organizations, addNote, removeNote } = useOrganizations(teamQueriesEnabled);
  const teamOrganization = organizations.find((org) => org.owner_id === userData?.firebase_uid) ?? organizations[0];
  const showTeamOption = BETA_ORGANIZATIONS_FEATURE_ENABLED && !!teamOrganization;
  const isAlreadyInTeam = !!(noteId && teamOrganization?.note_ids?.includes(noteId));
  const { folders, addNoteToFolder, removeNoteFromFolder, refetch } = useOrganizationFolders(
    teamOrganization?.id,
    teamQueriesEnabled && !!teamOrganization,
  );

  // The folder this note currently lives in (if any) — for defaulting the
  // Select and reconciling on "Update folder".
  const currentFolder = noteId ? folders.find((f) => f.note_ids.includes(noteId)) : undefined;

  // Default the Select to the note's current folder when filed, else General.
  // Re-sync whenever the modal opens or the note's folder membership changes.
  useEffect(() => {
    if (!open) return;
    setSelectedFolderId(currentFolder?.id ?? 'general');
  }, [open, currentFolder?.id]);

  const handleShare = async () => {
    setIsInviting(true);
    // Perform email invite
    try {
      await inviteToNote({
        headers: { authorization: `Bearer ${userToken}` },
        body: {
          emails: emailAddresses.split(',').map((email) => email.trim()),
          note_id: noteId || '',
          time_limit: accessDuration === 'unlimited' ? null : accessDuration,
        },
      });
    } catch {
      // mutateAsync always rejects on failure — the hook's onError handles feedback.
    }
  };

  const handleTeamShare = async () => {
    if (!teamOrganization || !noteId || isAlreadyInTeam) return;
    setIsTeamSharing(true);
    try {
      await addNote({ organizationId: teamOrganization.id, noteId });
      if (selectedFolderId !== 'general') {
        try {
          await addNoteToFolder({ organizationId: teamOrganization.id, folderId: selectedFolderId, noteId });
        } catch {
          // Roll back: note was added to team but folder filing failed — remove it
          // so it isn't silently left in General outside the intended folder.
          await removeNote({ organizationId: teamOrganization.id, noteId }).catch(() => null);
          throw new Error('folder');
        }
      }
      showAlert(`Note moved to ${teamOrganization.name}`, 'success');
      handleClose();
    } catch (error) {
      showAlert(getApiErrorMessage(error, 'Failed to move note to team. Try again later.'), 'error');
    } finally {
      setIsTeamSharing(false);
    }
  };

  const handleUpdateFolder = async () => {
    if (!teamOrganization || !noteId) return;
    setIsTeamSharing(true);
    try {
      await reconcileNoteFolder(folders, teamOrganization.id, noteId, selectedFolderId, {
        addNoteToFolder,
        removeNoteFromFolder,
      });
      showAlert('Folder updated.', 'success');
      handleClose();
    } catch (error) {
      showAlert(getApiErrorMessage(error, 'Failed to update folder. Try again later.'), 'error');
    } finally {
      setIsTeamSharing(false);
    }
  };

  // The created folder is returned so we can refresh the list and pre-select it.
  const handleFolderCreated = async (folder?: OrganizationFolder) => {
    setCreateFolderOpen(false);
    await refetch();
    if (folder) setSelectedFolderId(folder.id);
  };

  const handleTeamUnshare = async () => {
    if (!teamOrganization || !noteId || !isAlreadyInTeam) return;
    setIsTeamSharing(true);
    try {
      await removeNote({ organizationId: teamOrganization.id, noteId });
      // Best-effort: clear folder membership so stale IDs can't reappear if re-shared.
      const foldersWithNote = folders.filter((f) => f.note_ids.includes(noteId));
      await Promise.allSettled(
        foldersWithNote.map((folder) =>
          removeNoteFromFolder({ organizationId: teamOrganization.id, folderId: folder.id, noteId }),
        ),
      );
      showAlert(`Note removed from ${teamOrganization.name}`, 'success');
      handleClose();
    } catch (error) {
      showAlert(getApiErrorMessage(error, 'Failed to remove note from team. Try again later.'), 'error');
    } finally {
      setIsTeamSharing(false);
    }
  };

  // Handle public share generation
  const handlePublicShare = async () => {
    setIsPublicLoading(true);
    try {
      // Reset previous
      setPublicUrl('');
      setExpiresAt(null);
      // Build body
      const body: { time_limit?: string } = {};
      if (accessDuration !== 'unlimited' && accessDuration !== null) body.time_limit = accessDuration;
      const res = await fetch(`${import.meta.env.VITE_API_URL}/notes/${noteId}/share_public`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        // FastAPI returns `{ detail }`, not `{ message }` — reading only
        // `message` meant the fallback string always won and the backend's real
        // reason (seat limits, permissions, plan gating) never reached the user.
        throw new Error(getApiErrorMessage(err, 'Failed to generate public link'));
      }
      const data = await res.json();
      // Transform /shared/ to /public/ for the shareable URL
      const publicPath = data.public_url.replace('/shared/', '/public/');
      setPublicUrl(`${window.location.origin}${publicPath}`);
      setExpiresAt(data.expires_at);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      showAlert(errorMessage, 'error');
    } finally {
      setIsPublicLoading(false);
    }
  };

  // Copy the plain link, guarding against environments where the Clipboard API
  // is missing or denies permission (insecure context, older browser) so the
  // click handler never throws an unhandled rejection.
  const copyPlainLink = async (successMessage: string) => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      showAlert(successMessage, 'success');
    } catch {
      showAlert('Could not copy automatically — please copy the link manually', 'error');
    }
  };

  return (
    <>
      <CustomModal open={open} handleClose={handleClose}>
        <Box
          sx={{
            mb: 2,
            mt: isMobile ? 5 : 0,
            display: 'flex',
            gap: 2,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <Button
            variant={shareMode === 'public' ? 'outlined' : 'text'}
            onClick={() => {
              setShareMode('public');
              setEmailAddresses('');
            }}
            fullWidth={isMobile}
          >
            Public Link
          </Button>
          <Button
            variant={shareMode === 'email' ? 'outlined' : 'text'}
            onClick={() => {
              setShareMode('email');
              setPublicUrl('');
              setExpiresAt(null);
            }}
            fullWidth={isMobile}
          >
            Invite by Email
          </Button>
          {showTeamOption && (
            <Button
              variant={shareMode === 'team' ? 'outlined' : 'text'}
              onClick={() => {
                setShareMode('team');
                setEmailAddresses('');
                setPublicUrl('');
                setExpiresAt(null);
              }}
              fullWidth={isMobile}
            >
              Move to team
            </Button>
          )}
        </Box>

        {shareMode === 'team' ? (
          <Stack spacing={3}>
            <Typography sx={{ color: 'text.secondary' }}>
              {isAlreadyInTeam ? (
                <>
                  This note has been moved to <strong>{teamOrganization?.name}</strong>.
                </>
              ) : (
                <>
                  This note will be moved to <strong>{teamOrganization?.name}</strong>.
                </>
              )}
            </Typography>
            {folders.length > 0 && (
              <FormControl size="small" fullWidth>
                <InputLabel id="invite-folder-label">Folder</InputLabel>
                <Select
                  labelId="invite-folder-label"
                  label="Folder"
                  value={selectedFolderId}
                  onChange={(e) => setSelectedFolderId(e.target.value)}
                  disabled={isTeamSharing}
                >
                  <MenuItem value="general">General</MenuItem>
                  {folders.map((folder) => (
                    <MenuItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Button
              variant="text"
              size="small"
              startIcon={<CreateNewFolderOutlinedIcon />}
              onClick={() => setCreateFolderOpen(true)}
              disabled={isTeamSharing}
              sx={{ alignSelf: 'flex-start' }}
            >
              New folder
            </Button>
            <Stack
              direction={isMobile ? 'column' : 'row'}
              spacing={1.5}
              sx={{ alignSelf: isMobile ? 'stretch' : 'flex-start' }}
            >
              <Button
                variant="contained"
                color={isAlreadyInTeam ? 'error' : 'primary'}
                onClick={isAlreadyInTeam ? handleTeamUnshare : handleTeamShare}
                disabled={isTeamSharing || !teamOrganization}
                sx={{ minWidth: 'auto', px: 2 }}
                fullWidth={isMobile}
              >
                {isTeamSharing ? <CircularProgress size={24} /> : isAlreadyInTeam ? 'Remove from team' : 'Move'}
              </Button>
              {isAlreadyInTeam && folders.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={handleUpdateFolder}
                  disabled={isTeamSharing || !teamOrganization}
                  sx={{ minWidth: 'auto', px: 2 }}
                  fullWidth={isMobile}
                >
                  Update folder
                </Button>
              )}
            </Stack>
          </Stack>
        ) : shareMode === 'email' ? (
          <>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }} fontWeight={500}>
              By entering email addresses:
            </Typography>
            <Stack spacing={3}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Enter email addresses"
                  value={emailAddresses}
                  onChange={(e) => setEmailAddresses(e.target.value)}
                  sx={{ flex: 1 }}
                  slotProps={{
                    input: {
                      sx: { borderRadius: isMobile ? 8 : 100 },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleShare}
                  sx={{ minWidth: isMobile ? 'auto' : 100 }}
                  disabled={!emailAddresses || isInviting}
                  fullWidth={isMobile}
                >
                  {isInviting ? <CircularProgress size={24} /> : 'Share'}
                </Button>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: isMobile ? 'stretch' : 'center',
                  gap: 2,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <Typography
                  sx={{
                    color: 'text.secondary',
                    textAlign: isMobile ? 'left' : 'left',
                    flexShrink: 0,
                  }}
                >
                  The note will be shared for
                </Typography>
                <FormControl size="small" fullWidth={isMobile}>
                  <Select
                    value={accessDuration}
                    onChange={(e) => setAccessDuration(e.target.value as TimeLimit | 'unlimited')}
                    sx={{ borderRadius: isMobile ? 8 : 100 }}
                  >
                    <MenuItem value="7d">7 days</MenuItem>
                    <MenuItem value="14d">14 days</MenuItem>
                    <MenuItem value="30d">30 days</MenuItem>
                    <MenuItem value="unlimited">Unlimited</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Stack>
          </>
        ) : (
          <Stack spacing={3}>
            <Box
              sx={{
                display: 'flex',
                alignItems: isMobile ? 'stretch' : 'center',
                gap: 2,
                flexDirection: isMobile ? 'column' : 'row',
              }}
            >
              <Typography sx={{ flexShrink: 0 }}>The note will be shared for</Typography>
              <FormControl size="small" sx={{ minWidth: isMobile ? 'auto' : 120 }} fullWidth={isMobile}>
                <Select
                  value={accessDuration}
                  onChange={(e) => setAccessDuration(e.target.value as TimeLimit | 'unlimited')}
                  // Once the link exists its expiry is fixed server-side, and the
                  // "Generate Link" button is gone — leaving this editable let a
                  // user pick "7 days", see no error, and share a link that never
                  // had that expiry.
                  disabled={!!publicUrl}
                  sx={{ borderRadius: isMobile ? 8 : 100 }}
                >
                  <MenuItem value="unlimited">Unlimited</MenuItem>
                  <MenuItem value="7d">7 days</MenuItem>
                  <MenuItem value="14d">14 days</MenuItem>
                  <MenuItem value="30d">30 days</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {publicUrl ? (
              <>
                <TextField
                  fullWidth
                  label="Public URL"
                  InputProps={{ readOnly: true }}
                  value={publicUrl}
                  slotProps={{
                    input: {
                      sx: { borderRadius: isMobile ? 8 : 4 },
                    },
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    flexDirection: isMobile ? 'column' : 'row',
                  }}
                >
                  <Button variant="contained" onClick={() => copyPlainLink('Copied to clipboard')} fullWidth={isMobile}>
                    Copy to clipboard
                  </Button>
                </Box>
                {expiresAt && (
                  <Typography variant="body2" color="text.secondary">
                    Expires at: {new Date(expiresAt).toLocaleString()}
                  </Typography>
                )}
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handlePublicShare}
                disabled={isPublicLoading}
                sx={{
                  alignSelf: isMobile ? 'stretch' : 'flex-start',
                  minWidth: 'auto',
                  px: 2,
                }}
                fullWidth={isMobile}
              >
                {isPublicLoading ? <CircularProgress size={24} /> : 'Generate Link'}
              </Button>
            )}
          </Stack>
        )}
      </CustomModal>

      {teamOrganization && (
        <FolderShareModal
          open={createFolderOpen}
          onClose={() => setCreateFolderOpen(false)}
          organizationId={teamOrganization.id}
          memberEmails={teamOrganization.member_emails}
          ownerEmail={teamOrganization.owner_email}
          onSaved={handleFolderCreated}
        />
      )}
    </>
  );
};

export default InviteModal;
